import { useCallback, useRef, useState } from 'react';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { useDownloadAttachment } from '../App.hooks';

/* ------------------------------------------------------------------ */
/*  GraphQL queries (inline — no codegen required)                    */
/* ------------------------------------------------------------------ */

const GET_MODELS_FOR_LIBRARY_REPORT = gql`
    query getModelsForLibraryReport($after: String) {
        ModelList(first: 5000, after: $after) {
            nodes {
                id
                caches
                projectId
                updatedTime
            }
            pageInfo {
                hasNextPage
                endCursor
            }
        }
    }
`;

const GET_PROJECTS_WITH_CLIENT_FOR_LIBRARY_REPORT = gql`
    query getProjectsWithClientForLibraryReport {
        ProjectList(first: 10000) {
            nodes {
                id
                clientId
                client {
                    id
                    name
                }
            }
        }
    }
`;

/* ------------------------------------------------------------------ */
/*  Local cache helpers                                                */
/* ------------------------------------------------------------------ */

const FIL_CACHE_PREFIX = 'fil-health:';

type CachedHeathData = {
    FoundFamilyCount: number;
    NotFoundFamilyCount: number;
};

function readHeathCache(modelId: string, updatedTime: string): CachedHeathData | null {
    try {
        const raw = localStorage.getItem(`${FIL_CACHE_PREFIX}${modelId}:${updatedTime}`);
        if (!raw) return null;
        return JSON.parse(raw) as CachedHeathData;
    } catch {
        return null;
    }
}

function writeHeathCache(modelId: string, updatedTime: string, hd: CachedHeathData): void {
    try {
        localStorage.setItem(`${FIL_CACHE_PREFIX}${modelId}:${updatedTime}`, JSON.stringify(hd));
    } catch {
        // quota exceeded — silently ignore
    }
}

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type FILPeriod = 'week' | 'month' | 'quarter';

export const FIL_PERIOD_OPTIONS: { id: FILPeriod; label: string; days: number }[] = [
    { id: 'week',    label: '1 week',   days: 7  },
    { id: 'month',   label: '1 month',  days: 30 },
    { id: 'quarter', label: '3 months', days: 90 },
];

export type ClientFoundInLibraryRow = {
    clientId: string;
    clientName: string;
    modelCount: number;
    foundFamilies: number;
    totalFamilies: number;
    familyFoundPct: number | null;
};

export type FoundInLibraryReport = {
    rows: ClientFoundInLibraryRow[];
    enterpriseFamilyFoundPct: number | null;
    totalModelsAnalyzed: number;
    totalModelsSkipped: number;
    /** Number of download failures. */
    totalModelsFailed: number;
    /** Number of models served from localStorage cache. */
    totalFromCache: number;
};

export type UseFoundInLibraryReportOptions = {
    period?: FILPeriod;
};

export type UseFoundInLibraryReportResult = {
    generate: () => void;
    cancel: () => void;
    report: FoundInLibraryReport | null;
    phase: 'idle' | 'fetching-projects' | 'fetching-models' | 'downloading' | 'done';
    progress: {
        fetchedModels: number;
        eligibleModels: number;
        downloadedModels: number;
        failedModels: number;
        fromCache: number;
    };
    error: string | null;
};

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

export function useFoundInLibraryReport(
    options?: UseFoundInLibraryReportOptions
): UseFoundInLibraryReportResult {
    const periodId = options?.period ?? 'quarter';
    const periodDays = FIL_PERIOD_OPTIONS.find((p) => p.id === periodId)?.days ?? 90;

    const [phase, setPhase] = useState<UseFoundInLibraryReportResult['phase']>('idle');
    const [progress, setProgress] = useState({ fetchedModels: 0, eligibleModels: 0, downloadedModels: 0, failedModels: 0, fromCache: 0 });
    const [report, setReport] = useState<FoundInLibraryReport | null>(null);
    const [error, setError] = useState<string | null>(null);

    const cancelRef = useRef(false);

    const download = useDownloadAttachment();
    const [fetchModelsPage] = useLazyQuery(GET_MODELS_FOR_LIBRARY_REPORT, { fetchPolicy: 'network-only' });
    const { refetch: refetchProjects } = useQuery(GET_PROJECTS_WITH_CLIENT_FOR_LIBRARY_REPORT, {
        fetchPolicy: 'network-only',
        skip: true, // only fetch on demand
    });

    const generate = useCallback(async () => {
        cancelRef.current = false;
        setPhase('fetching-projects');
        setProgress({ fetchedModels: 0, eligibleModels: 0, downloadedModels: 0, failedModels: 0, fromCache: 0 });
        setReport(null);
        setError(null);

        try {
            /* ---- 1. Fetch all projects to build projectId → client map ---- */
            const projectsResult = await refetchProjects();
            if (cancelRef.current) return;

            const projectNodes: any[] = projectsResult?.data?.ProjectList?.nodes ?? [];
            const projectClientMap = new Map<string, { clientId: string; clientName: string }>();
            for (const p of projectNodes) {
                if (p?.id && p?.clientId) {
                    projectClientMap.set(p.id, {
                        clientId: p.clientId,
                        clientName: p.client?.name ?? p.clientId,
                    });
                }
            }

            /* ---- 2. Paginate through all models to find eligible ones ---- */
            setPhase('fetching-models');

            const cutoffEpoch = Math.floor(Date.now() / 1000) - periodDays * 24 * 60 * 60;

            type EligibleModel = { id: string; caches: string; projectId: string; updatedTime: string };
            const eligibleModels: EligibleModel[] = [];
            let cursor: string | null = null;
            let totalFetched = 0;

            while (true) {
                if (cancelRef.current) return;

                const result: any = await fetchModelsPage({ variables: { after: cursor } });
                const data: any = result?.data;
                const pageError: any = result?.error;
                if (pageError) throw new Error(pageError.message);

                const nodes: any[] = data?.ModelList?.nodes ?? [];
                totalFetched += nodes.length;

                for (const node of nodes) {
                    if (!node?.caches) continue;
                    const projectId: string = node.projectId ?? '';
                    if (!projectClientMap.has(projectId)) continue;

                    const updatedEpoch = node.updatedTime ? Number(node.updatedTime) : 0;
                    if (updatedEpoch < cutoffEpoch) continue;

                    eligibleModels.push({ id: node.id ?? '', caches: node.caches, projectId, updatedTime: String(node.updatedTime ?? '') });
                }

                setProgress((p) => ({ ...p, fetchedModels: totalFetched, eligibleModels: eligibleModels.length }));

                const pageInfo: { hasNextPage: boolean; endCursor?: string | null } | undefined = data?.ModelList?.pageInfo;
                if (!pageInfo?.hasNextPage) break;
                cursor = pageInfo.endCursor ?? null;
                if (!cursor) break;
            }

            if (cancelRef.current) return;

            /* ---- 3. Download cache files & aggregate by client ---- */
            setPhase('downloading');

            const CONCURRENCY = 6;
            const MAX_RETRIES = 2;

            // clientId → accumulator
            type ClientAccum = {
                clientName: string;
                modelCount: number;
                foundFamilies: number;
                totalFamilies: number;
            };
            const clientMap = new Map<string, ClientAccum>();

            let downloaded = 0;
            let failed = 0;
            let skipped = 0;
            let fromCache = 0;
            let nextIndex = 0;

            const accumulateHD = (clientId: string, clientName: string, hd: CachedHeathData) => {
                const foundFam = Number(hd.FoundFamilyCount    ?? 0);
                const notFam   = Number(hd.NotFoundFamilyCount ?? 0);
                const existing = clientMap.get(clientId);
                if (existing) {
                    existing.modelCount++;
                    existing.foundFamilies += foundFam;
                    existing.totalFamilies += foundFam + notFam;
                } else {
                    clientMap.set(clientId, {
                        clientName,
                        modelCount: 1,
                        foundFamilies: foundFam,
                        totalFamilies: foundFam + notFam,
                    });
                }
            };

            const processModel = async (model: EligibleModel) => {
                const clientInfo = projectClientMap.get(model.projectId);
                if (!clientInfo) { skipped++; return; }

                // Serve from localStorage cache when available — keyed on
                // model id + updatedTime so it auto-invalidates on re-upload
                const cached = readHeathCache(model.id, model.updatedTime);
                if (cached) {
                    fromCache++;
                    accumulateHD(clientInfo.clientId, clientInfo.clientName, cached);
                    downloaded++;
                    return;
                }

                for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
                    if (cancelRef.current) return;
                    try {
                        const raw = await download(model.caches);
                        let parsed: any;
                        try {
                            parsed = JSON.parse(atob(raw));
                        } catch {
                            parsed = JSON.parse(raw);
                        }

                        const hd: CachedHeathData | undefined = parsed?.HeathData;
                        if (!hd) { skipped++; return; }

                        writeHeathCache(model.id, model.updatedTime, hd);
                        accumulateHD(clientInfo.clientId, clientInfo.clientName, hd);
                        downloaded++;
                        return;
                    } catch {
                        if (attempt < MAX_RETRIES) {
                            await new Promise((r) => setTimeout(r, 300 * Math.pow(3, attempt)));
                        }
                    }
                }
                failed++;
            };

            const worker = async () => {
                while (!cancelRef.current) {
                    const i = nextIndex++;
                    if (i >= eligibleModels.length) return;
                    await processModel(eligibleModels[i]);
                    setProgress((p) => ({
                        ...p,
                        downloadedModels: downloaded,
                        failedModels: failed,
                        fromCache,
                    }));
                }
            };

            await Promise.all(
                Array.from({ length: Math.min(CONCURRENCY, eligibleModels.length || 1) }, () => worker())
            );

            if (cancelRef.current) return;

            /* ---- 4. Build report ---- */
            const pct = (found: number, total: number) =>
                total > 0 ? Math.round((found / total) * 1000) / 10 : null;

            const rows: ClientFoundInLibraryRow[] = Array.from(clientMap.entries())
                .map(([clientId, acc]) => ({
                    clientId,
                    clientName:       acc.clientName,
                    modelCount:       acc.modelCount,
                    foundFamilies:    acc.foundFamilies,
                    totalFamilies:    acc.totalFamilies,
                    familyFoundPct:   pct(acc.foundFamilies, acc.totalFamilies),
                }))
                .sort((a, b) => (b.familyFoundPct ?? -1) - (a.familyFoundPct ?? -1));

            const totFam   = rows.reduce((s, r) => s + r.totalFamilies, 0);
            const foundFam = rows.reduce((s, r) => s + r.foundFamilies, 0);

            setReport({
                rows,
                enterpriseFamilyFoundPct: pct(foundFam, totFam),
                totalModelsAnalyzed: downloaded,
                totalModelsSkipped:  skipped,
                totalModelsFailed:   failed,
                totalFromCache:      fromCache,
            });
            setPhase('done');
        } catch (err: any) {
            if (!cancelRef.current) {
                setError(err?.message ?? 'Unknown error generating report');
                setPhase('idle');
            }
        }
    }, [periodDays, fetchModelsPage, refetchProjects, download]);

    const cancel = useCallback(() => {
        cancelRef.current = true;
        setPhase('idle');
    }, []);

    return { generate, cancel, report, phase, progress, error };
}
